import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { RootState } from "../../../utils/store";
import { ChatList } from "./ChatList";
import { ChatMessageForm } from "./ChatMessageForm";
import {
  useCreateChatMutation,
  useCreateMessageMutation,
  useDeleteChatMutation,
} from "../../../api/chatApi";
import { ChatRequestTaskQue } from "../../../api/chatRequestTaskQueue";
import type {
  Chat,
  MessageInteractionPhase,
  Message,
  ModelOption,
} from "./types";
import { fixLongName } from "../../../products/copy-assistant/utils";
import { ChatMessageList } from "./ChatMessageList";
import { useSelector } from "react-redux";

type ChatLayoutProps = {
  chats: string; // JSON string
  modelOptions: string; // JSON string
};

export const ChatLayout: React.FC<ChatLayoutProps> = ({
  chats: chatList,
  modelOptions,
}) => {
  const getInitialChatId = (): number | null => {
    const parsedChats = JSON.parse(chatList || "[]");
    const urlParams = new URLSearchParams(window.location.search);
    const chatParam = parseInt(urlParams.get("id"));
    if (chatParam && parsedChats.find((c: Chat) => c.id === chatParam)) {
      return chatParam;
    }
    return parsedChats.length > 0 ? parsedChats[0].id : null;
  };

  const parsedModelOptions: ModelOption[] = JSON.parse(modelOptions || "[]");

  const token = useSelector((state: RootState) => state.auth.token);
  const [
    createChatRequest,
    { isLoading: isCreatingChat },
  ] = useCreateChatMutation();
  const [createMessage] = useCreateMessageMutation();
  const [deleteChatRequest] = useDeleteChatMutation();
  const [chats, setChats] = useState<Chat[]>(JSON.parse(chatList || "[]"));
  const [activeChatId, setActiveChatId] = useState<number | null>(
    getInitialChatId()
  );
  const [messageStep, setMessageStep] = useState<number>(0); // Used to animate non-streaming-responses
  const [interactionPhase, setInteractionPhase] = useState<
    MessageInteractionPhase
  >("idle");
  const [assistantStreamVisible, setAssistantStreamVisible] = useState(false);

  const messageScrollAreaRef = useRef<HTMLDivElement>(null);
  const activeChat = chats.find((c) => c.id === activeChatId) || null;
  const initialModel = activeChat
    ? parsedModelOptions.find((item) => item.model_name == activeChat.modelName)
    : null;
  const chatRequestTaskQue = useMemo(() => {
    if (token) return new ChatRequestTaskQue(token);
  }, [token]);

  useLayoutEffect(() => {
    const scrollRef = messageScrollAreaRef.current;
    if (!scrollRef) {
      return;
    }
    scrollRef.scrollTop = scrollRef.scrollHeight;
  }, [activeChatId]);

  useEffect(() => {
    if (activeChatId) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set("id", activeChatId.toString());
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${urlParams}`
      );
    }
  }, [activeChatId]);

  useEffect(() => {
    setInteractionPhase("idle");
  }, [activeChatId]);

  useEffect(() => {
    if (interactionPhase === "creating message") {
      setAssistantStreamVisible(false);
    }
  }, [interactionPhase]);

  const handleChatSelect = (chatId: number): void => {
    setActiveChatId(chatId);
    setMessageStep(0);
  };

  const handleChatCreate = async (): Promise<void> => {
    if (isCreatingChat) {
      return;
    }

    const emptyChat = chats.find((chat) => chat.messages.length === 0);
    if (emptyChat) {
      setActiveChatId(emptyChat.id);
      setMessageStep(0);
      return;
    }

    try {
      const response = await createChatRequest({}).unwrap();

      setChats([
        {
          id: response.id,
          modelName: response.model_name,
          messages: [],
        },
        ...chats,
      ]);
      setActiveChatId(response.id);
      setMessageStep(0);
    } catch (error) {
      console.error(error);
    }
  };

  const handleChatDelete = async (chatId: number): Promise<void> => {
    if (isCreatingChat) {
      return;
    }

    setChats((l) => l.filter((item) => item.id != chatId));

    try {
      await deleteChatRequest({
        chat_id: chatId,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleMessageAdded = (newMessage: Message): void => {
    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat.id === activeChatId
          ? { ...chat, messages: [...chat.messages, newMessage] }
          : chat
      )
    );
  };

  const handleAssistantStreamUpdate = useCallback(
    (assistant: Message): void => {
      setAssistantStreamVisible(true);
      setChats((prev) =>
        prev.map((chat) =>
          chat.id === activeChatId
            ? {
                ...chat,
                messages: (() => {
                  const next = [...chat.messages];
                  const last = next[next.length - 1];
                  if (last?.role === "assistant") {
                    next[next.length - 1] = assistant;
                  } else {
                    next.push(assistant);
                  }
                  return next;
                })(),
              }
            : chat
        )
      );
    },
    [activeChatId]
  );

  const handleNonStreamResponse = (): void => setMessageStep(1);

  return (
    <article className="txu-form tw-grid tw-grid-rows-[min-content_auto] tw-grid-cols-[auto_1fr] tw-w-full tw-h-full-min-header tw-gap-6 tw-pt-8 tw-mx-auto !tw-pl-6">
      <ChatList
        className="tw-row-start-2 tw-col-start-1"
        summaries={chats.map((chat) => ({
          name: chat.messages[0]
            ? fixLongName(chat.messages[0].text, 24)
            : "Unnamed chat",
          id: chat.id,
          timeSinceActive: chat.messages[chat.messages.length - 1]?.timeStamp,
        }))}
        onChatSelect={handleChatSelect}
        onChatCreate={handleChatCreate}
        onChatDelete={handleChatDelete}
        activeChatId={activeChatId || undefined}
      />

      <header className="tw-w-full tw-flex-none tw-col-start-2">
        <h1 className="tw-m-0 tw-text-2xl">Chat</h1>
      </header>

      <div className="tw-col-start-2 tw-min-h-0 tw-h-full tw-p-8 tw-w-full tw-min-h-0 tw-border-t tw-border-l tw-flex tw-flex-1 tw-flex-col tw-justify-between tw-items-center tw-overflow-hidden">
        {activeChat ? (
          <>
            <section
              ref={messageScrollAreaRef}
              className="tw-w-full tw-overflow-y-auto tw-flex tw-justify-center"
              style={{ scrollbarGutter: "stable" }}
            >
              <div className="tw-max-w-[1000px] tw-pr-2 tw-grow tw-w-full tw-min-h-0 tw-pb-32 ">
                <ChatMessageList
                  key={activeChatId}
                  messages={activeChat.messages}
                  messageStep={messageStep}
                  assistantReplyStreaming={
                    interactionPhase === "awaiting reply"
                  }
                />

                {/* loading animation */}
                {interactionPhase === "awaiting reply" &&
                  !assistantStreamVisible && (
                    <div className="tw-ml-4 tw-flex tw-gap-2">
                      <i
                        className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-gray-400 tw-transition tw-animate-pulse"
                        style={{ animationDuration: "1s" }}
                      />
                      <i
                        className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-gray-400 tw-transition tw-animate-pulse"
                        style={{
                          animationDuration: "1s",
                          animationDelay: "100ms",
                        }}
                      />
                      <i
                        className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-gray-400 tw-transition tw-animate-pulse"
                        style={{
                          animationDuration: "1s",
                          animationDelay: "200ms",
                        }}
                      />
                    </div>
                  )}
              </div>
            </section>
            <ChatMessageForm
              chatId={activeChatId}
              modelOptions={parsedModelOptions}
              initialModel={initialModel}
              chatRequestTaskQue={chatRequestTaskQue}
              interactionPhase={interactionPhase}
              createMessage={createMessage}
              setResponsePhase={setInteractionPhase}
              onMessageAdded={handleMessageAdded}
              onAssistantStreamUpdate={handleAssistantStreamUpdate}
              onNonStreamResponse={handleNonStreamResponse}
            />
          </>
        ) : (
          <p>No chat selected. Please select a chat or create a new one.</p>
        )}
      </div>
    </article>
  );
};
